/**
 * Game clock with clamped delta time.
 *
 * Returns a clamped dt each frame to prevent force stacking issues
 * when Babylon/Havok physics steps once per scene.render().
 * Max dt is capped at ~33ms (1/30s) to ensure stable behavior on frame drops.
 */
export class Clock {
  /** Maximum allowed dt in seconds (prevents physics instability) */
  readonly maxDt: number;

  private lastTime = 0;
  private started = false;

  /** Frames rendered this session */
  frameCount = 0;
  /** Time since last render frame (clamped for physics) */
  renderDt = 0;
  /** Raw time since last frame (unclamped, for timer) */
  rawDt = 0;
  /** Current FPS (updated each frame) */
  fps = 0;

  private fpsAccum = 0;
  private fpsFrames = 0;
  private lastFpsUpdate = 0;

  constructor(maxFps = 30) {
    this.maxDt = 1 / maxFps;
  }

  /**
   * Call at the start of each frame.
   * Returns clamped dt for this frame's update.
   */
  tick(timestamp: number): number {
    if (!this.started) {
      this.lastTime = timestamp;
      this.lastFpsUpdate = timestamp;
      this.started = true;
      return 0;
    }

    const rawDt = (timestamp - this.lastTime) / 1000;
    const dt = Math.min(rawDt, this.maxDt); // Clamp to max ~33ms
    this.lastTime = timestamp;
    this.rawDt = rawDt;    // Unclamped for timer
    this.renderDt = rawDt; // Debug display shows real dt

    // FPS calculation
    this.fpsAccum += rawDt;
    this.fpsFrames++;
    if (timestamp - this.lastFpsUpdate >= 500) {
      this.fps = Math.round(this.fpsFrames / this.fpsAccum);
      this.fpsAccum = 0;
      this.fpsFrames = 0;
      this.lastFpsUpdate = timestamp;
    }

    this.frameCount++;

    return dt;
  }

  /** Reset clock state (for restarts) */
  reset(): void {
    this.started = false;
    this.frameCount = 0;
  }
}
